import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useApolloClient } from "@apollo/client";
import axios from "../../../../api/axios";
import BasicButton from "../../../Buttons/BasicButton";
import { GET_USER_BALANCE } from "../../../../api/graphql/graph";
import { GET_USER_NOTIFICATIONS } from "../../../../api/graphql/users";
import { GET_USER_PAYMENTS } from "../../../../api/graphql/payments";
import { CryptoDeposit, CryptoDepositState } from "./types";
import styles from "./ledgerPayment.module.scss";

// While the window is open the backend reads the chain on every status
// request, so this polling is what credits a payment within seconds. A late
// payment can still land after the window closes, hence the slow poll there.
const POLL_MS: Partial<Record<CryptoDepositState, number>> = {
  AWAITING_PAYMENT: 5000,
  CHECKING: 5000,
  EXPIRED: 15000,
};

const SUPPORT_EMAIL = "info@app-installs.net";

const useCountdown = (until: string) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return Math.max(0, new Date(until).getTime() - now);
};

const formatCountdown = (ms: number) => {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const shortHash = (hash: string) => `${hash.slice(0, 8)}…${hash.slice(-6)}`;

const usd = (value: number) =>
  `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 2C3.34315 2 2 3.34315 2 5V15C2 16.6569 3.34315 18 5 18H6V19C6 20.6569 7.34315 22 9 22H19C20.6569 22 22 20.6569 22 19V9C22 7.34315 20.6569 6 19 6H18V5C18 3.34315 16.6569 2 15 2H5ZM16 5C16 4.44772 15.5523 4 15 4H5C4.44772 4 4 4.44772 4 5V15C4 15.5523 4.44772 16 5 16H15C15.5523 16 16 15.5523 16 15V5ZM18 8H19C19.5523 8 20 8.44772 20 9V19C20 19.5523 19.5523 20 19 20H9C8.44771 20 8 19.5523 8 19V18H15C16.6569 18 18 16.6569 18 15V8Z"
    />
  </svg>
);

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  return (
    <button
      type="button"
      className={styles.copy}
      onClick={copy}
      aria-label={`Copy ${label}`}
    >
      {copied ? <span className={styles.copied}>Copied</span> : <CopyIcon />}
    </button>
  );
};

const TxLink = ({ transfer }: { transfer: CryptoDeposit["transfer"] }) =>
  transfer ? (
    <a
      className={styles.txLink}
      href={transfer.explorerUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {shortHash(transfer.txHash)} ↗
    </a>
  ) : null;

type Props = {
  deposit: CryptoDeposit;
  onClose: () => void;
  onNewPayment: () => void;
};

/**
 * The payment step of Add funds → Crypto: where to send the USDT, then what
 * happened to it. The modal can be closed at any point; reopening it brings
 * an open payment back (GET /payments/crypto/active).
 */
export const LedgerPaymentStep = ({
  deposit: initial,
  onClose,
  onNewPayment,
}: Props) => {
  const [deposit, setDeposit] = useState(initial);
  const [changing, setChanging] = useState(false);
  const client = useApolloClient();
  const remaining = useCountdown(deposit.expiresAt);
  const state: CryptoDepositState =
    deposit.state === "AWAITING_PAYMENT" && remaining === 0
      ? "EXPIRED"
      : deposit.state;
  const interval = POLL_MS[state];

  useEffect(() => {
    if (!interval) return;
    let busy = false;
    const timer = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        const { data } = await axios.get(`/payments/crypto/${deposit.hashId}`);
        if (data?.crypto) setDeposit(data.crypto);
      } catch {
        // The next tick asks again.
      } finally {
        busy = false;
      }
    }, interval);
    return () => clearInterval(timer);
  }, [deposit.hashId, interval]);

  // Reopening the modal brings an unpaid top-up back on purpose (the client
  // may have paid already), so a different amount means dropping this one
  // first. Money sent for it anyway is still credited for a day.
  const changeAmount = async () => {
    setChanging(true);
    try {
      await axios.post(`/payments/crypto/${deposit.hashId}/cancel`);
      onNewPayment();
    } catch {
      // Most likely the transfer has just arrived: show where it stands.
      try {
        const { data } = await axios.get(`/payments/crypto/${deposit.hashId}`);
        if (data?.crypto) setDeposit(data.crypto);
      } catch {
        // The polling catches up.
      }
      setChanging(false);
    }
  };

  useEffect(() => {
    if (deposit.state !== "PAID") return;
    client
      .refetchQueries({
        include: [GET_USER_BALANCE, GET_USER_NOTIFICATIONS, GET_USER_PAYMENTS],
      })
      .catch(() => undefined);
  }, [deposit.state, client]);

  if (state === "PAID") {
    return (
      <div className={styles.step}>
        <div className={`${styles.result} ${styles.success}`}>
          <span className={styles.resultTitle}>
            {deposit.testnet ? "Test payment received" : "Payment received"}
          </span>
          <span className={styles.resultAmount}>
            {usd(deposit.creditedUsd ?? 0)} added to your balance
          </span>
          {deposit.transfer && (
            <span className={styles.resultMeta}>
              {deposit.transfer.amount} USDT ·{" "}
              <TxLink transfer={deposit.transfer} />
            </span>
          )}
        </div>
        <div className={styles.buttons}>
          {deposit.receiptUrl && (
            <BasicButton
              type="button"
              size="large"
              title="View receipt"
              variant="secondary"
              cls={styles.button}
              onClick={() =>
                window.open(deposit.receiptUrl as string, "_blank", "noopener")
              }
            />
          )}
          <BasicButton
            type="button"
            size="large"
            title="Done"
            variant="primary"
            cls={styles.button}
            onClick={onClose}
          />
        </div>
      </div>
    );
  }

  if (state === "ON_HOLD" || state === "REJECTED" || state === "EXPIRED") {
    const copy = {
      ON_HOLD: {
        tone: styles.warning,
        title: "Payment under review",
        text: "Your transfer has arrived and is going through a manual compliance check. We'll add it to your balance once the check is done and let you know.",
      },
      REJECTED: {
        tone: styles.error,
        title: "Payment not accepted",
        text: `We couldn't accept this transfer. Please write to ${SUPPORT_EMAIL} and include the transaction link.`,
      },
      EXPIRED: {
        tone: styles.neutral,
        title: "Payment window closed",
        text: `If you've already sent ${deposit.amount} USDT, we'll still add it to your balance when it arrives. Otherwise start a new payment.`,
      },
    }[state];

    return (
      <div className={styles.step}>
        <div className={`${styles.result} ${copy.tone}`}>
          <span className={styles.resultTitle}>{copy.title}</span>
          <span className={styles.resultText}>{copy.text}</span>
          <span className={styles.resultMeta}>
            Payment ID <span className={styles.mono}>{deposit.hashId}</span>
            {deposit.transfer && (
              <>
                {" · "}
                {deposit.transfer.amount} USDT ·{" "}
                <TxLink transfer={deposit.transfer} />
              </>
            )}
          </span>
        </div>
        <div className={styles.buttons}>
          <BasicButton
            type="button"
            size="large"
            title="Close"
            variant={state === "EXPIRED" ? "secondary" : "primary"}
            cls={styles.button}
            onClick={onClose}
          />
          {state === "EXPIRED" && (
            <BasicButton
              type="button"
              size="large"
              title="New payment"
              variant="primary"
              cls={styles.button}
              onClick={onNewPayment}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.step}>
      {deposit.testnet && (
        <p className={styles.testNotice}>
          Test payment on the TRON Nile testnet. Send test USDT from the Nile
          faucet; real USDT sent here now is not credited.
        </p>
      )}
      <div className={styles.summary}>
        <div className={styles.qr}>
          <QRCodeSVG
            value={deposit.address}
            size={112}
            marginSize={0}
            title="Wallet address"
          />
        </div>
        <div className={styles.amountBlock}>
          <span className={styles.label}>Send exactly</span>
          <span className={styles.amountRow}>
            <span className={styles.amount}>{deposit.amount} USDT</span>
            <CopyButton value={deposit.amount} label="amount" />
          </span>
          <span className={styles.network}>
            {deposit.testnet
              ? "TRON Nile testnet (TRC20)"
              : "TRON network (TRC20)"}
          </span>
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>To address</span>
        <span className={styles.addressRow}>
          <span className={styles.address}>{deposit.address}</span>
          <CopyButton value={deposit.address} label="address" />
        </span>
      </div>

      <ul className={styles.notes}>
        <li>
          Send only USDT on the TRON{deposit.testnet ? " Nile test" : ""}{" "}
          network. Another token or network won't arrive.
        </li>
        <li>
          If your exchange takes its withdrawal fee out of the amount, add the
          fee on top. We credit what arrives.
        </li>
      </ul>

      <p className={styles.paymentId}>
        Payment ID <span className={styles.mono}>{deposit.hashId}</span>.
        Mention it if you write to us about this payment.
      </p>

      <div className={styles.status} role="status">
        {state === "CHECKING" ? (
          <>
            <span className={styles.statusText}>
              <span className={styles.pulse} aria-hidden="true" />
              Transfer received, checking it
            </span>
            <TxLink transfer={deposit.transfer} />
          </>
        ) : (
          <>
            <span className={styles.statusText}>
              <span className={styles.pulse} aria-hidden="true" />
              Waiting for your transfer
            </span>
            <span className={styles.countdown}>
              {formatCountdown(remaining)} left
            </span>
          </>
        )}
      </div>

      <div className={styles.buttons}>
        {state === "AWAITING_PAYMENT" && (
          <BasicButton
            type="button"
            size="large"
            title="Change amount"
            variant="secondary"
            cls={styles.button}
            disabled={changing}
            onClick={changeAmount}
          />
        )}
        <BasicButton
          type="button"
          size="large"
          title="Close"
          variant="secondary"
          cls={styles.button}
          onClick={onClose}
        />
      </div>
    </div>
  );
};
